import { z } from 'zod';
import { IsoTimestamp, QuizId, SchoolId, SessionId, UserId } from './common.js';
export const ResultDeliveryStatus = z.enum([
    'pending',
    'processing',
    'retrying',
    'delivered',
    'failed',
]);
export const PublishResultRequest = z.object({ reason: z.string().trim().min(3).max(500) });
export const RetryResultDeliveryRequest = z.object({
    reason: z.string().trim().min(3).max(500),
});
export const ResultScoreComponent = z.object({
    name: z.enum(['objective', 'essay']),
    score: z.number().nonnegative(),
    maximumScore: z.number().positive(),
});
/**
 * Per-question answer detail pushed back to AIMSIS alongside the aggregate
 * score. Only questions that carry an AIMSIS source id are included, so the
 * `question_source_id` always maps to a real `eo_question` row. Fields are
 * snake_case because this block is serialized straight onto the AIMSIS
 * ingestion payload (see AimsisResultIngestionPayload).
 */
export const ResultAnswerDetail = z.object({
    question_source_id: z.number().int().positive(),
    kind: z.enum(['mcq', 'multipleAnswer', 'essay', 'trueFalse', 'shortAnswer', 'matching', 'labelContent']),
    score: z.number().nonnegative(),
    maximum_score: z.number().nonnegative(),
    // Objective auto-graded correctness; omitted for essay/manual questions.
    correct: z.boolean().optional(),
    // mcq / multipleAnswer: the AIMSIS option ids the student picked.
    selected_option_source_ids: z.array(z.number().int().positive()).max(64).optional(),
    // trueFalse: the boolean the student chose.
    boolean_value: z.boolean().optional(),
    // essay / shortAnswer: the free-text response.
    text: z.string().max(50_000).optional(),
    // matching: the student's left→right pairing.
    pairs: z.array(z.object({
        left: z.string().min(1).max(64),
        right: z.string().min(1).max(64),
    })).max(50).optional(),
});
export const ResultDeliveryScoreSnapshot = z.object({
    autoScore: z.number().nonnegative(),
    manualScore: z.number().nonnegative(),
    objectiveMaximum: z.number().nonnegative(),
    essayMaximum: z.number().nonnegative(),
    totalScore: z.number().nonnegative(),
    maxScore: z.number().positive(),
    submittedAt: IsoTimestamp,
    finalizedAt: IsoTimestamp,
    tenantSourceId: z.number().int().positive(),
    quizSourceId: z.number().int().positive(),
    studentSourceId: z.number().int().positive(),
    academicSubdomain: z.string().trim().min(1).max(63),
    // Per-question answers, captured immutably at finalization. Present only for
    // AIMSIS-sourced questions; empty/absent when no question has a source id.
    answers: z.array(ResultAnswerDetail).max(200).optional(),
});
export const ResultDeliveryQuery = z.object({
    status: ResultDeliveryStatus.optional(),
    quizId: QuizId.optional(),
    studentId: UserId.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.string().trim().min(1).max(512).optional(),
});
export const ResultDeliveryRecord = z.object({
    id: z.string().min(1).max(128),
    schoolId: SchoolId,
    tenantSourceId: z.number().int().positive(),
    academicSubdomain: z.string().trim().min(1).max(63),
    quizId: QuizId,
    quizSourceId: z.number().int().positive(),
    sessionId: SessionId,
    studentId: UserId,
    studentSourceId: z.number().int().positive(),
    attemptNumber: z.number().int().positive().default(1),
    resultVersion: z.number().int().positive().default(1),
    scoreComponents: z.array(ResultScoreComponent).min(1).max(2),
    scoreSnapshot: ResultDeliveryScoreSnapshot.optional(),
    totalScore: z.number().nonnegative(),
    maxScore: z.number().positive(),
    submittedAt: IsoTimestamp,
    finalizedAt: IsoTimestamp,
    idempotencyKey: z.string().regex(/^[A-Za-z0-9._:-]{1,128}$/),
    checksum: z.string().regex(/^[a-f0-9]{64}$/),
    status: ResultDeliveryStatus,
    reason: z.string().max(500),
    createdAt: IsoTimestamp,
    updatedAt: IsoTimestamp,
    nextAttemptAt: IsoTimestamp.optional(),
    attemptCount: z.number().int().nonnegative().default(0),
    leaseId: z.string().uuid().optional(),
    leaseExpiresAt: IsoTimestamp.optional(),
    externalStatus: z.enum([
        'accepted',
        'already-applied',
        'validation-failed',
        'mapping-missing',
        'retryable-error',
    ]).optional(),
    externalResultId: z.string().max(128).optional(),
    predecessorDeliveryId: z.string().min(1).max(128).optional(),
    correctionOfSessionId: SessionId.optional(),
    correctionCommandId: z.string().uuid().optional(),
    reconciliationRequired: z.boolean().optional(),
    correctionReason: z.string().max(500).optional(),
    deliveredAt: IsoTimestamp.optional(),
    lastErrorCode: z.string().max(100).optional(),
    lastErrorMessage: z.string().max(300).optional(),
    retryRequestedAt: IsoTimestamp.optional(),
    retryRequestedBy: UserId.optional(),
    retryReason: z.string().max(500).optional(),
});
export const AimsisResultIngestionPayload = z.object({
    tenant_source_id: z.number().int().positive(),
    quiz_source_id: z.number().int().positive(),
    student_source_id: z.number().int().positive(),
    cbt_attempt_id: z.string().regex(/^[A-Za-z0-9._:-]{1,128}$/),
    score_components: z.array(z.object({
        name: z.enum(['objective', 'essay']),
        score: z.number().nonnegative(),
        maximum_score: z.number().positive(),
    })).min(1).max(2),
    maximum_score: z.number().positive(),
    finalized: z.literal(true),
    finalized_at: IsoTimestamp,
    idempotency_key: z.string().regex(/^[A-Za-z0-9._:-]{1,128}$/),
    // Optional per-question answer detail. AIMSIS's ingestion endpoint allowlists
    // fields, so this is emitted only once the AIMSIS side accepts it (gated by
    // AIMSIS_RESULT_INCLUDE_ANSWERS on the CBT producer).
    answers: z.array(ResultAnswerDetail).max(200).optional(),
});
//# sourceMappingURL=result-delivery.js.map